import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CalendarIcon, PlayIcon, History, ArrowRight } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays, isAfter, isBefore, subDays } from 'date-fns';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BacktestProviderToggle } from './BacktestProviderToggle';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BacktestHistoryView } from './BacktestHistoryView';

interface BacktestSettingsProps {
  settings: {
    mode?: string;
    provider?: string;
    startDate?: string;
    endDate?: string;
    marketEnvironment?: string;
    timeframe?: string;
    initialCapital?: string;
    includeCommission?: boolean;
    commissionRate?: string;
    includeSlippage?: boolean;
    slippageModel?: string;
    slippageValue?: string;
    executionDelay?: string;
    [key: string]: any;
  };
  onSettingsChange: (settings: any) => void;
  onRunBacktest?: () => void;
}

export const BacktestSettings = ({ 
  settings = {}, 
  onSettingsChange, 
  onRunBacktest 
}: BacktestSettingsProps) => {
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  // Maintain local state in sync with props to ensure UI reflects current values
  const [localSettings, setLocalSettings] = useState(settings);
  
  // Function to get the application timeline's current date
  // This would typically come from your application's context or config
  const getApplicationTimeline = useCallback(() => {
    // Use the current date instead of a hardcoded future date
    // This ensures we can't select future dates that don't have market data
    return new Date();
  }, []);
  
  // Update local state when props change
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);
  
  // Helper function to safely update settings
  const updateSettings = useCallback((updatedValues: Record<string, any>) => {
    // Update local state immediately for responsive UI
    setLocalSettings(prevSettings => {
      const newSettings = {
        ...prevSettings,
        ...updatedValues,
        // Keep the provider if it's being explicitly set, otherwise keep current value
        provider: updatedValues.hasOwnProperty('provider') 
          ? updatedValues.provider 
          : prevSettings.provider || 'alpaca',
        timeframe: updatedValues.timeframe || prevSettings.timeframe || 'day' // Ensure timeframe is always present
      };
      return newSettings;
    });
    
    // Notify parent component with updated values
    if (onSettingsChange && typeof onSettingsChange === 'function') {
      onSettingsChange({
        ...settings,
        ...updatedValues,
        // Keep the provider if it's being explicitly set, otherwise keep current value
        provider: updatedValues.hasOwnProperty('provider') 
          ? updatedValues.provider 
          : settings.provider || 'alpaca',
        timeframe: updatedValues.timeframe || settings.timeframe || 'day' // Ensure timeframe is always present
      });
    }
  }, [settings, onSettingsChange]);
  
  // Set a default provider if not already set
  useEffect(() => {
    // Only run if we have valid settings and the change handler
    if (settings && onSettingsChange && typeof onSettingsChange === 'function' && !settings.provider) {
      updateSettings({ provider: 'alpaca' });
    }
  }, [settings, onSettingsChange, updateSettings]);

  // Validate dates to ensure they make sense for backtesting
  const validateDates = useCallback(() => {
    if (!localSettings.startDate || !localSettings.endDate) return;
    
    const startDate = new Date(localSettings.startDate);
    const endDate = new Date(localSettings.endDate);
    
    // Use the application's timeline context
    const appTimeline = getApplicationTimeline();
    
    // Function to get last market close in the app's timeline context
    const getLastMarketClose = (): Date => {
      const yesterday = new Date(appTimeline);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // If yesterday was a weekend, adjust further back
      const dayOfWeek = yesterday.getDay(); // 0 = Sunday, 6 = Saturday
      
      if (dayOfWeek === 0) { // Sunday -> use Friday
        yesterday.setDate(yesterday.getDate() - 2);
      } else if (dayOfWeek === 6) { // Saturday -> use Friday
        yesterday.setDate(yesterday.getDate() - 1);
      }
      
      return yesterday;
    };
    
    const lastMarketClose = getLastMarketClose();
    const thirtyDaysAgo = new Date(appTimeline);
    thirtyDaysAgo.setDate(appTimeline.getDate() - 30);
    
    // FIX: More permissive validation - allow up to tomorrow
    const futureLimit = new Date(appTimeline);
    futureLimit.setDate(appTimeline.getDate() + 1); // Allow up to one day into "future"
    
    if (isAfter(endDate, futureLimit)) {
      setDateError(`End date is too far in the future. Use ${format(futureLimit, 'yyyy-MM-dd')} or earlier for available market data.`);
      return;
    }
    
    // FIX: Only warn about today's date, don't treat recent past dates as errors
    if (endDate.toDateString() === appTimeline.toDateString()) {
      setDateError("For optimal backtesting, use yesterday's close or earlier to ensure complete market data.");
      return;
    }
    
    // Check if start date is after end date
    if (isAfter(startDate, endDate)) {
      setDateError('Start date must be before end date.');
      return;
    }
    
    // For Polygon.io free plan - warn if dates are outside the last 30 days
    if (localSettings.provider === 'polygon' && isBefore(startDate, thirtyDaysAgo)) {
      setDateError('Warning: Free Polygon.io plan only provides data for the last 30 days. Consider switching to Alpaca or upgrading Polygon.io.');
      return;
    }
    
    // Clear error if all checks pass
    setDateError(null);
  }, [localSettings.startDate, localSettings.endDate, localSettings.provider, getApplicationTimeline]);
  
  // Validate dates whenever they change or provider changes
  useEffect(() => {
    validateDates();
  }, [localSettings.startDate, localSettings.endDate, localSettings.provider, validateDates]);

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      // Prevent selecting future dates using the application timeline context
      if (isAfter(date, getApplicationTimeline())) {
        setDateError('Start date cannot be in the future.');
        return;
      }
      
      updateSettings({ startDate: format(date, 'yyyy-MM-dd') });
      setStartDateOpen(false);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      // Get the last known valid market day from the application's context
      // This is determined by examining the actual data availability
      // Use the validateDates function which already has the app timeline context logic
      const today = new Date();
      
      // Prevent selecting future dates by using the application's timeline context
      // The validateDates function already checks against app timeline
      updateSettings({ endDate: format(date, 'yyyy-MM-dd') });
      setEndDateOpen(false);
      
      // Run validation which will set appropriate warnings if date is invalid
      setTimeout(() => validateDates(), 0);
    }
  };

  const handleMarketEnvChange = (value: string) => {
    updateSettings({ marketEnvironment: value });
  };
  
  const handleTimeframeChange = (value: string) => {
    updateSettings({ timeframe: value });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label className="text-base font-semibold">Testing Mode</Label>
        <div className="relative group">
          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
            View your backtest history
            <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-900 rotate-45"></div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-1 bg-gradient-to-r from-blue-500/80 to-indigo-500/80 text-white border-none shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-600/90 hover:to-indigo-600/90" 
            onClick={() => setShowHistoryDialog(true)}
          >
            <History className="h-4 w-4" />
            <span>History</span>
            <ArrowRight className="h-4 w-4 ml-1 animate-pulse" />
          </Button>
        </div>
      </div>
      
      <Select 
        value={localSettings.mode} 
        onValueChange={(value) => updateSettings({ mode: value })}
      >
        <SelectTrigger className="w-full rounded-xl">
          <SelectValue placeholder="Select testing mode" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="backtest">Historical Backtest</SelectItem>
          <SelectItem value="paper">Paper Trading</SelectItem>
          <SelectItem value="live">Live Trading</SelectItem>
          <SelectItem value="historical">Historical Analysis</SelectItem>
        </SelectContent>
      </Select>

      {/* Provider toggle */}
      <BacktestProviderToggle 
        provider={localSettings.provider as 'polygon' | 'alpaca' || 'polygon'}
        onProviderChange={(provider) => updateSettings({ provider })}
      />

      {/* Date pickers */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start-date">Start Date</Label>
          <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
            <PopoverTrigger asChild>
              <Button
                id="start-date"
                variant="outline"
                className="w-full justify-start text-left font-normal rounded-xl"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {localSettings.startDate ? new Date(localSettings.startDate).toLocaleDateString() : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" style={{ zIndex: 9999 }}>
              <Calendar
                mode="single"
                selected={localSettings.startDate ? new Date(localSettings.startDate) : undefined}
                onSelect={handleStartDateChange}
                initialFocus
                className="rounded-md border z-50"
                disabled={(date) => {
                  // Use application timeline context instead of real current date
                  const appTimelineLimit = getApplicationTimeline();
                  return isAfter(date, appTimelineLimit);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="end-date">End Date</Label>
          <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
            <PopoverTrigger asChild>
              <Button
                id="end-date"
                variant="outline"
                className="w-full justify-start text-left font-normal rounded-xl"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {localSettings.endDate ? new Date(localSettings.endDate).toLocaleDateString() : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" style={{ zIndex: 9999 }}>
              <Calendar
                mode="single"
                selected={localSettings.endDate ? new Date(localSettings.endDate) : undefined}
                onSelect={handleEndDateChange}
                initialFocus
                className="rounded-md border z-50"
                disabled={(date) => {
                  // Use application timeline context instead of real current date
                  const appTimelineLimit = getApplicationTimeline();
                  return isAfter(date, appTimelineLimit);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Add information about date policy for backtesting */}
      <Alert variant="default" className="bg-blue-50 border-blue-200 text-blue-700 text-sm">
        <AlertCircle className="h-4 w-4 mr-2" />
        <AlertDescription>
          For optimal backtesting, the system automatically uses data up to the last market close, 
          ensuring you're working with complete and finalized market data.
        </AlertDescription>
      </Alert>

      {/* Timeframe Selection for Polygon.io */}
      <div className="space-y-2">
        <Label htmlFor="timeframe">Data Timeframe</Label>
        <Select 
          value={localSettings.timeframe || 'day'} 
          onValueChange={handleTimeframeChange}
        >
          <SelectTrigger id="timeframe" className="w-full rounded-xl">
            <SelectValue placeholder="Select timeframe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="minute">1 Minute <span className="text-xs text-muted-foreground ml-1">(Advanced Plan)</span></SelectItem>
            <SelectItem value="5minute">5 Minute <span className="text-xs text-muted-foreground ml-1">(Likely Advanced Plan)</span></SelectItem>
            <SelectItem value="15minute">15 Minute <span className="text-xs text-muted-foreground ml-1">(Starter/Dev Plan)</span></SelectItem>
            <SelectItem value="hour">1 Hour <span className="text-xs text-muted-foreground ml-1">(Advanced Plan)</span></SelectItem>
            <SelectItem value="day">Day <span className="text-xs text-muted-foreground ml-1">(Any Plan)</span></SelectItem>
            <SelectItem value="week">Week <span className="text-xs text-muted-foreground ml-1">(Any Plan)</span></SelectItem>
            <SelectItem value="month">Month <span className="text-xs text-muted-foreground ml-1">(Any Plan)</span></SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Note: Access to granular timeframes (Minute, Hour) for extended historical periods typically requires a Polygon.io "Stocks Advanced" plan.
          <br />
          Starter/Developer plans may offer 15-minute data for recent periods but usually provide Daily data for full history.
        </p>
      </div>

      {/* Market environment */}
      <div className="space-y-2">
        <Label htmlFor="market-env">Market Environment</Label>
        <Select 
          value={localSettings.marketEnvironment} 
          onValueChange={handleMarketEnvChange}
        >
          <SelectTrigger id="market-env" className="w-full rounded-xl">
            <SelectValue placeholder="Market conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Market Conditions</SelectItem>
            <SelectItem value="bull">Bull Market</SelectItem>
            <SelectItem value="bear">Bear Market</SelectItem>
            <SelectItem value="volatile">High Volatility</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Initial capital */}
      <div className="space-y-2">
        <Label htmlFor="initial-capital">Initial Capital</Label>
        <Input
          type="number"
          id="initial-capital"
          placeholder="Enter initial capital"
          value={localSettings.initialCapital}
          onChange={(e) => updateSettings({ initialCapital: e.target.value })}
          className="rounded-xl"
        />
      </div>

      {/* Commission settings */}
      <div className="flex items-center space-x-2 mb-2">
        <Switch
          checked={localSettings.includeCommission || false}
          onCheckedChange={(checked) => updateSettings({ includeCommission: checked })}
        />
        <Label>Include Commission</Label>
      </div>

      {localSettings.includeCommission && (
        <div className="space-y-2">
          <Label htmlFor="commission-rate">Commission Rate (%)</Label>
          <Input
            type="number"
            id="commission-rate"
            placeholder="Enter commission rate"
            value={localSettings.commissionRate}
            onChange={(e) => updateSettings({ commissionRate: e.target.value })}
            className="rounded-xl"
          />
        </div>
      )}

      {/* Slippage settings */}
      <div className="flex items-center space-x-2 mb-2">
        <Switch
          checked={localSettings.includeSlippage || false}
          onCheckedChange={(checked) => updateSettings({ includeSlippage: checked })}
        />
        <Label>Include Slippage</Label>
      </div>

      {localSettings.includeSlippage && (
        <div className="space-y-2">
          <Label htmlFor="slippage-model">Slippage Model</Label>
          <Select
            value={localSettings.slippageModel}
            onValueChange={(value) => updateSettings({ slippageModel: value })}
          >
            <SelectTrigger className="w-full rounded-xl">
              <SelectValue placeholder="Select slippage model" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixed">Fixed</SelectItem>
              <SelectItem value="percentage">Percentage</SelectItem>
            </SelectContent>
          </Select>

          <Label htmlFor="slippage-value">Slippage Value</Label>
          <Input
            type="number"
            id="slippage-value"
            placeholder="Enter slippage value"
            value={localSettings.slippageValue}
            onChange={(e) => updateSettings({ slippageValue: e.target.value })}
            className="rounded-xl"
          />
        </div>
      )}

      {/* Execution delay */}
      <div className="space-y-2">
        <Label htmlFor="execution-delay">Execution Delay (ms)</Label>
        <Input
          type="number"
          id="execution-delay"
          placeholder="Enter execution delay"
          value={localSettings.executionDelay}
          onChange={(e) => updateSettings({ executionDelay: e.target.value })}
          className="rounded-xl"
        />
      </div>

      {/* Date error display */}
      {dateError && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>{dateError}</AlertDescription>
        </Alert>
      )}

      {/* Run backtest button removed to avoid duplication with the calculator icon button */}
      
      {/* Backtest History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-6xl h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Backtest History</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-100">
              <h3 className="text-sm font-medium text-blue-800 mb-1">View and manage your backtest history</h3>
              <p className="text-xs text-blue-600">Access all your previous backtests, filter results, and analyze performance over time.</p>
            </div>
            <BacktestHistoryView />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
